// The ingests currently running, so more than one can be watched at once.
//
// This used to be a single `?run=` URL parameter, which meant uploading a second
// sheet silently stopped tracking the first: the param was overwritten and the
// earlier ingest finished with nobody watching. Uploading several sheets in a row
// is the normal way to seed a rate book, so one slot was the wrong shape.
//
// Kept in localStorage rather than component state because an ingest outlives the
// page — a conversion runs for minutes and a person will navigate away, refresh,
// or close the tab and come back. Keyed per tenant so two tenants in one browser
// cannot see each other's runs.
//
// The FILENAME is stored beside the run id, and that is the whole reason this is
// not just a list of ids: with three ingests in flight, "Reading page 19 (1 of 6)"
// is useless unless it says which document it is reading.
import { useCallback, useEffect, useState } from 'react';
const KEY_PREFIX = 'specra.pricingSheetIngests.';
// A cap, because this list is written by uploads and read on every render: a
// person who uploads forty sheets should not carry forty entries forever. The
// oldest fall off, and they are only a WATCH LIST — the runs themselves are
// server-side and a finished sheet is in the review queue regardless.
const MAX_TRACKED = 12;
function keyFor(tenantId) {
    return KEY_PREFIX + tenantId;
}
function read(tenantId) {
    if (!tenantId) {
        return [];
    }
    try {
        const raw = window.localStorage.getItem(keyFor(tenantId));
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        // Filtered on read: a stored shape from an older version of this code must
        // not crash the page it is rendered on.
        return parsed.filter((v)=>typeof v === 'object' && v !== null && typeof v.runId === 'string');
    } catch  {
        // Unparseable or storage-denied. An empty watch list is a degraded view, not
        // a broken one — the reviews are still in the queue.
        return [];
    }
}
function write(tenantId, runs) {
    try {
        window.localStorage.setItem(keyFor(tenantId), JSON.stringify(runs.slice(0, MAX_TRACKED)));
    } catch  {
    // Private browsing, quota. Tracking is a convenience; losing it must not
    // break the upload that just succeeded.
    }
}
export function useActiveIngests(tenantId) {
    const [runs, setRuns] = useState(()=>read(tenantId));
    // Re-read when the tenant changes, so switching tenants does not show the
    // previous one's ingests.
    useEffect(()=>{
        setRuns(read(tenantId));
    }, [
        tenantId
    ]);
    const track = useCallback((runId, filename)=>{
        setRuns((prev)=>{
            if (prev.some((r)=>r.runId === runId)) {
                return prev;
            }
            // Newest first: the thing a person just did is the thing they are looking
            // for.
            const next = [
                {
                    filename,
                    runId,
                    startedAt: Date.now()
                },
                ...prev
            ].slice(0, MAX_TRACKED);
            write(tenantId, next);
            return next;
        });
    }, [
        tenantId
    ]);
    const untrack = useCallback((runId)=>{
        setRuns((prev)=>{
            const next = prev.filter((r)=>r.runId !== runId);
            write(tenantId, next);
            return next;
        });
    }, [
        tenantId
    ]);
    return {
        runs,
        track,
        untrack
    };
}
